import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import baseUrl from '../../apiBaseUrl/APIConfig';
import { setupInterceptorsTo } from '../authenticationInterceptors/Interceptors';
import { ImageUploadResponse } from './Model';
import { headers } from '../../components/Utility';

interface ImageUploadState {
  imageUploadResponse: ImageUploadResponse | null;
  loading: boolean;
  progress: number;
  message: string;
  status: string;
}

const initialState: ImageUploadState = {
  imageUploadResponse: null,
  loading: false,
  progress: 0,
  message: '',
  status: '0',
};

export const uploadImage = createAsyncThunk(
  'imageUpload/upload',
  async (formData: FormData, { dispatch, rejectWithValue }) => {
    const updatedHeaders = { ...headers, 'Content-type': 'multipart/form-data' };
    try {
      const response = await setupInterceptorsTo(axios).post(
        `${baseUrl.imageUploadUrl}/storageapp/api/upload-image/`,
        formData,
        {
          headers: updatedHeaders,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUploadProgress: (progress: any) => {
            const { loaded, total } = progress;
            const percentage = Math.floor((loaded / (total || 1)) * 100);
            dispatch(imageUploadProgress(percentage)); // Update progress in real-time
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Could not upload the Image!';
      const status = String(error.response?.data?.status || '0');
      return rejectWithValue({ message, status });
    }
  },
);

// --- Slice ---
const imageUploadSlice = createSlice({
  name: 'imageUpload',
  initialState,
  reducers: {
    imageUploadProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    updateImageUploadAPIResMsg: (state) => {
      state.message = '';
      state.status = '0';
    },
    clearImageUploadAPIRes: (state) => {
      state.imageUploadResponse = null;
      state.message = '';
      state.status = '0';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadImage.pending, (state) => {
        state.loading = true;
        state.progress = 0;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.loading = false;
        state.imageUploadResponse = action.payload;
        state.message = action.payload.message;
        state.status = String(action.payload.status);
      })
      .addCase(uploadImage.rejected, (state, action: any) => {
        state.loading = false;
        state.imageUploadResponse = null;
        state.message = action.payload?.message || 'Error';
        state.status = action.payload?.status || '0';
      });
  },
});

export const { imageUploadProgress, updateImageUploadAPIResMsg, clearImageUploadAPIRes } =
  imageUploadSlice.actions;
export default imageUploadSlice.reducer;
